import { createWriteStream } from "node:fs";
import { unlink } from "node:fs/promises";
import path from "node:path";
import { Readable, Transform } from "node:stream";
import { pipeline } from "node:stream/promises";
import type { ReadableStream as NodeReadableStream } from "node:stream/web";
import { z } from "zod";

import {
  primeUiComponentCandidatesResponseSchema,
  primeUiComponentPropsValidateResponseSchema,
  primeUiCreateExportResponseSchema,
  primeUiComponentExportResponseSchema,
  primeUiExportsResponseSchema,
  primeUiIssueReportSubmitResponseSchema,
  primeUiManagedSearchOverviewResponseSchema,
  primeUiManagedSearchRuntimeDeleteResponseSchema,
  primeUiManagedSearchRuntimeMutationResponseSchema,
  primeUiProjectApiPagesResponseSchema,
  primeUiProjectApiVariantResponseSchema,
  primeUiProjectApiVariantSummaryResponseSchema,
  primeUiProjectApiVariantsResponseSchema,
  primeUiProjectDescriptionSchema,
  primeUiProjectInfoSchema,
  primeUiProjectPageDetailsSchema,
} from "../lib/api-v1-contract.js";
import { ensureDir } from "../lib/fs.js";
import type {
  PrimeUiComponentCandidatesInput,
  PrimeUiComponentCandidatesResult,
  PrimeUiComponentPropsValidateInput,
  PrimeUiComponentPropsValidateResult,
  PrimeUiCreateProjectApiPageInput,
  PrimeUiCreateProjectApiVariantInput,
  PrimeUiComponentExportManifest,
  PrimeUiCreateComponentExportOptions,
  PrimeUiCreateExportResult,
  PrimeUiExport,
  PrimeUiManagedSearchOverviewResult,
  PrimeUiManagedSearchRuntimeApiMutationResult,
  PrimeUiManagedSearchRuntimeDeleteResult,
  PrimeUiManagedSearchRuntimeStatus,
  PrimeUiIssueReportSubmitResult,
  PrimeUiProjectApiPagesResult,
  PrimeUiProjectApiVariantResult,
  PrimeUiProjectApiVariantSummaryResult,
  PrimeUiProjectApiVariantsResult,
  PrimeUiProjectDescriptionResult,
  PrimeUiProjectPageDetailsResult,
  PrimeUiProjectInfo,
  PrimeUiSubmitIssueReportInput,
} from "../types.js";
import type { ProjectDataProvider } from "./project-data-provider.js";

type ApiProjectDataProviderOptions = {
  apiKey?: string;
  baseUrl?: string;
};

export const DEFAULT_API_BASE_URL = "https://app.primeui.com/";
const ZIP_CONTENT_TYPES = [
  "application/zip",
  "application/octet-stream",
  "application/x-zip-compressed",
];

class PrimeUiApiContractError extends Error {
  constructor(endpoint: string, details: string) {
    super(`PrimeUI API contract mismatch for "${endpoint}": ${details}`);
    this.name = "PrimeUiApiContractError";
  }
}

export const normalizePrimeUiApiRoot = (rawBaseUrl?: string): string => {
  const normalizedBase = rawBaseUrl?.trim() || DEFAULT_API_BASE_URL;
  const parsed = new URL(normalizedBase);
  const normalizedPath = parsed.pathname.replace(/\/+$/, "");

  if (normalizedPath.endsWith("/api/v1")) {
    parsed.pathname = `${normalizedPath}/`;
    return parsed.toString();
  }

  parsed.pathname = `${normalizedPath}/api/v1/`.replace(
    "//api/v1/",
    "/api/v1/",
  );
  return parsed.toString();
};

export const buildPrimeUiApiUrl = (apiRoot: string, endpoint: string): string =>
  new URL(endpoint, apiRoot).toString();

const isJsonContentType = (contentType: string): boolean =>
  contentType.includes("application/json") || contentType.includes("+json");

const isZipContentType = (contentType: string): boolean =>
  ZIP_CONTENT_TYPES.some((allowedType) => contentType.includes(allowedType));

function normalizePropsValidationId(
  propsValidationId: string | undefined,
): string | undefined {
  if (typeof propsValidationId === "undefined") {
    return undefined;
  }

  const trimmed = propsValidationId.trim();
  if (!trimmed) {
    throw new Error("propsValidationId must be a non-empty string.");
  }

  return trimmed;
}

const looksLikeZipArchive = (buffer: Buffer): boolean => {
  if (buffer.length < 4) {
    return false;
  }

  if (buffer[0] !== 0x50 || buffer[1] !== 0x4b) {
    return false;
  }

  const signature = `${buffer[2]}:${buffer[3]}`;
  return signature === "3:4" || signature === "5:6" || signature === "7:8";
};

const createZipSignatureGuard = (endpoint: string): Transform => {
  let signature = Buffer.alloc(0);
  let validated = false;

  return new Transform({
    transform(chunk, _encoding, callback) {
      const chunkBuffer = Buffer.isBuffer(chunk) ? chunk : Buffer.from(chunk);

      if (!validated) {
        const requiredBytes = Math.max(0, 4 - signature.length);
        if (requiredBytes > 0) {
          signature = Buffer.concat([
            signature,
            chunkBuffer.subarray(0, requiredBytes),
          ]);
        }

        if (signature.length >= 4) {
          if (!looksLikeZipArchive(signature)) {
            callback(
              new PrimeUiApiContractError(
                endpoint,
                "response body is not a valid zip archive",
              ),
            );
            return;
          }

          validated = true;
        }
      }

      callback(null, chunkBuffer);
    },
    flush(callback) {
      if (!validated) {
        callback(
          new PrimeUiApiContractError(
            endpoint,
            "response body is not a valid zip archive",
          ),
        );
        return;
      }

      callback();
    },
  });
};

export class ApiProjectDataProvider implements ProjectDataProvider {
  private readonly apiKey?: string;
  private readonly apiRoot: string;

  constructor(options: ApiProjectDataProviderOptions) {
    this.apiKey = options.apiKey;
    this.apiRoot = normalizePrimeUiApiRoot(options.baseUrl);
  }

  async getProjectInfo(): Promise<PrimeUiProjectInfo> {
    return this.requestJson("project", primeUiProjectInfoSchema);
  }

  async getProjectDescription(): Promise<PrimeUiProjectDescriptionResult> {
    return this.requestJson(
      "project/description",
      primeUiProjectDescriptionSchema,
    );
  }

  async upsertProjectDescription(
    projectDescription: string,
  ): Promise<PrimeUiProjectDescriptionResult> {
    return this.requestJson(
      "project/description",
      primeUiProjectDescriptionSchema,
      this.buildJsonRequestInit({
        projectDescription,
      }),
    );
  }

  async listProjectPages(): Promise<PrimeUiProjectApiPagesResult> {
    return this.requestJson(
      "project/pages",
      primeUiProjectApiPagesResponseSchema,
    );
  }

  async getProjectPage(pageId: string): Promise<PrimeUiProjectApiPagesResult> {
    return this.requestJson(
      `project/pages/${encodeURIComponent(pageId)}`,
      primeUiProjectApiPagesResponseSchema,
    );
  }

  async createProjectPage(
    input: PrimeUiCreateProjectApiPageInput,
  ): Promise<PrimeUiProjectApiPagesResult> {
    return this.requestJson(
      "project/pages",
      primeUiProjectApiPagesResponseSchema,
      this.buildJsonRequestInit(input),
    );
  }

  async listProjectPageVariants(
    pageId: string,
  ): Promise<PrimeUiProjectApiVariantsResult> {
    return this.requestJson(
      `project/pages/${encodeURIComponent(pageId)}/variants`,
      primeUiProjectApiVariantsResponseSchema,
    );
  }

  async getProjectPageVariant(
    pageId: string,
    variantId: string,
  ): Promise<PrimeUiProjectApiVariantResult> {
    return this.requestJson(
      `project/pages/${encodeURIComponent(pageId)}/variants/${encodeURIComponent(variantId)}`,
      primeUiProjectApiVariantResponseSchema,
    );
  }

  async createProjectPageVariant(
    pageId: string,
    input: PrimeUiCreateProjectApiVariantInput,
  ): Promise<PrimeUiProjectApiVariantSummaryResult> {
    return this.requestJson(
      `project/pages/${encodeURIComponent(pageId)}/variants`,
      primeUiProjectApiVariantSummaryResponseSchema,
      this.buildJsonRequestInit(input),
    );
  }

  async setProjectPageActiveVariant(
    pageId: string,
    variantId: string,
  ): Promise<PrimeUiProjectApiPagesResult> {
    return this.requestJson(
      `project/pages/${encodeURIComponent(pageId)}/active-variant`,
      primeUiProjectApiPagesResponseSchema,
      this.buildJsonRequestInit({
        variantId,
      }),
    );
  }

  async submitIssueReport(
    input: PrimeUiSubmitIssueReportInput,
  ): Promise<PrimeUiIssueReportSubmitResult> {
    return this.requestJson(
      "issue-reports",
      primeUiIssueReportSubmitResponseSchema,
      this.buildJsonRequestInit(input),
    );
  }

  async getSearchOverview(): Promise<PrimeUiManagedSearchOverviewResult> {
    return this.requestJson(
      "project/search",
      primeUiManagedSearchOverviewResponseSchema,
    );
  }

  async createSearchEnvironment(params: {
    name: string;
    projectId: string;
    slug: string;
  }): Promise<PrimeUiManagedSearchRuntimeApiMutationResult> {
    return this.requestJson(
      "project/search/runtimes",
      primeUiManagedSearchRuntimeMutationResponseSchema,
      this.buildJsonRequestInit(params),
    );
  }

  async updateSearchEnvironment(params: {
    projectId: string;
    runtimeSlug: string;
    name?: string;
    status?: PrimeUiManagedSearchRuntimeStatus;
  }): Promise<PrimeUiManagedSearchRuntimeApiMutationResult> {
    return this.requestJson(
      `project/search/runtimes/${encodeURIComponent(params.runtimeSlug)}`,
      primeUiManagedSearchRuntimeMutationResponseSchema,
      this.buildJsonRequestInit(
        {
          projectId: params.projectId,
          ...(params.name === undefined ? {} : { name: params.name }),
          ...(params.status === undefined ? {} : { status: params.status }),
        },
        {
          method: "PATCH",
        },
      ),
    );
  }

  async deleteSearchEnvironment(params: {
    projectId: string;
    runtimeSlug: string;
  }): Promise<PrimeUiManagedSearchRuntimeDeleteResult> {
    return this.requestJson(
      `project/search/runtimes/${encodeURIComponent(params.runtimeSlug)}`,
      primeUiManagedSearchRuntimeDeleteResponseSchema,
      this.buildJsonRequestInit(
        {
          projectId: params.projectId,
        },
        {
          method: "DELETE",
        },
      ),
    );
  }

  async getProjectPageBySlug(
    slug: string,
  ): Promise<PrimeUiProjectPageDetailsResult> {
    const encodedSlug = encodeURIComponent(slug);
    return this.requestJson(
      `project/page?slug=${encodedSlug}`,
      primeUiProjectPageDetailsSchema,
    );
  }

  async getComponentCandidates(
    input: PrimeUiComponentCandidatesInput,
  ): Promise<PrimeUiComponentCandidatesResult> {
    return this.requestJson(
      "component-planning/candidates",
      primeUiComponentCandidatesResponseSchema,
      this.buildJsonRequestInit(input),
    );
  }

  async validateComponentProps(
    input: PrimeUiComponentPropsValidateInput,
  ): Promise<PrimeUiComponentPropsValidateResult> {
    return this.requestJson(
      "component-planning/props/validate",
      primeUiComponentPropsValidateResponseSchema,
      this.buildJsonRequestInit(input),
    );
  }

  async listExports(): Promise<PrimeUiExport[]> {
    const response = await this.requestJson(
      "project/exports",
      primeUiExportsResponseSchema,
    );
    return response.exports;
  }

  async createExport(): Promise<PrimeUiCreateExportResult> {
    return this.requestJson(
      "project/exports",
      primeUiCreateExportResponseSchema,
      {
        method: "POST",
      },
    );
  }

  async createComponentExport(
    componentId: string,
    options: PrimeUiCreateComponentExportOptions = {},
  ): Promise<PrimeUiComponentExportManifest> {
    const propsValidationId = normalizePropsValidationId(
      options.propsValidationId,
    );
    return this.requestJson(
      `component-registry/components/${encodeURIComponent(componentId)}/exports`,
      primeUiComponentExportResponseSchema,
      propsValidationId
        ? this.buildJsonRequestInit({ propsValidationId })
        : {
            method: "POST",
          },
    );
  }

  /**
   * Consumer-side runtime contract for GET /api/v1/project/exports/:exportId/download.
   * Primary Studio runtime entrypoint: apps/studio/src/tanstack/routes/api.v1.project.exports.$exportId.download.ts
   * Legacy migration reference: apps/studio/src/app/api/v1/project/exports/[exportId]/download/route.ts
   * Keep content-type and archive format checks synchronized with the producer response.
   */
  async downloadExportArchive(
    exportId: string,
    destinationPath: string,
  ): Promise<void> {
    const endpoint = `project/exports/${encodeURIComponent(exportId)}/download`;
    const apiKey = this.requireApiKey();
    const response = await fetch(this.buildUrl(endpoint), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
    });

    if (!response.ok) {
      const details = await this.readError(response);
      throw new Error(
        `PrimeUI API request failed (${response.status}) while downloading export "${exportId}": ${details}`,
      );
    }

    const contentType = response.headers.get("content-type") ?? "";
    if (!isZipContentType(contentType)) {
      throw new PrimeUiApiContractError(
        endpoint,
        `expected zip content-type but got "${contentType || "unknown"}"`,
      );
    }

    if (!response.body) {
      throw new PrimeUiApiContractError(endpoint, "response body is empty");
    }

    await ensureDir(path.dirname(destinationPath));

    const zipStream = Readable.fromWeb(
      response.body as unknown as NodeReadableStream,
    );
    const signatureGuard = createZipSignatureGuard(endpoint);
    const fileStream = createWriteStream(destinationPath);

    try {
      await pipeline(zipStream, signatureGuard, fileStream);
    } catch (error) {
      await unlink(destinationPath).catch(() => undefined);
      throw error;
    }
  }

  private buildUrl(endpoint: string): string {
    return buildPrimeUiApiUrl(this.apiRoot, endpoint);
  }

  private buildJsonRequestInit(
    payload: unknown,
    overrides: RequestInit = {},
  ): RequestInit {
    return {
      ...overrides,
      method: overrides.method ?? "POST",
      headers: {
        ...(overrides.headers ?? {}),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    };
  }

  private async requestJson<T>(
    endpoint: string,
    schema: z.ZodType<T>,
    requestInit: RequestInit = {},
  ): Promise<T> {
    const apiKey = this.requireApiKey();
    const method = requestInit.method ?? "GET";
    const response = await fetch(this.buildUrl(endpoint), {
      ...requestInit,
      method,
      headers: {
        ...(requestInit.headers ?? {}),
        Authorization: `Bearer ${apiKey}`,
      },
    });

    if (!response.ok) {
      const details = await this.readError(response);
      throw new Error(
        `PrimeUI API request failed (${response.status}) for "${endpoint}": ${details}`,
      );
    }

    const contentType = response.headers.get("content-type") ?? "";
    if (!isJsonContentType(contentType)) {
      throw new PrimeUiApiContractError(
        endpoint,
        `expected JSON content-type but got "${contentType || "unknown"}"`,
      );
    }

    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      throw new PrimeUiApiContractError(
        endpoint,
        "response body is not valid JSON",
      );
    }

    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      const details = parsed.error.issues
        .map((issue) => `${issue.path.join(".") || "<root>"}: ${issue.message}`)
        .join("; ");
      throw new PrimeUiApiContractError(endpoint, details);
    }

    return parsed.data;
  }

  private async readError(response: Response): Promise<string> {
    const contentType = response.headers.get("content-type") ?? "";

    try {
      if (isJsonContentType(contentType)) {
        const body = (await response.json()) as {
          message?: string;
          code?: string;
        };
        if (body?.message && body?.code) {
          return `${body.code}: ${body.message}`;
        }
        if (body?.message) {
          return body.message;
        }
      } else {
        const bodyText = await response.text();
        if (bodyText.trim()) {
          return bodyText.trim();
        }
      }
    } catch {
      // No-op: fall through to generic status text.
    }

    return response.statusText || "Unknown error";
  }

  private requireApiKey(): string {
    const apiKey = this.apiKey?.trim();
    if (!apiKey) {
      throw new Error("PRIMEUI_API_KEY is required to call PrimeUI API tools.");
    }

    return apiKey;
  }
}
